import React, { Component } from 'react';
import styles from './styles.module.scss';
import subscriptionsService from '../../services/subscriptions';

class SubscriptionCheckout extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            error: null,
            productsLoading: true,
            products: [],
            selectedProductId: null,
            billingInterval: 'month', // 'month' or 'year'
            showIntervalToggle: false
        };
    }

    componentDidMount() {
        this.fetchProducts();
    }

    fetchProducts = async () => {
        try {
            this.setState({ productsLoading: true });
            const products = await subscriptionsService.getAvailableProducts();

            console.log('Received products from API:', products);

            // Show toggle if at least one product has both monthly and yearly pricing
            const showIntervalToggle = products.some(p =>
                p.prices && p.prices.month && p.prices.year
            );

            console.log('Toggle check - show toggle if at least one product has both intervals:', showIntervalToggle);

            this.setState({
                products,
                selectedProductId: null,
                productsLoading: false,
                showIntervalToggle
            });
        } catch (err) {
            console.error('Error fetching products:', err);
            this.setState({
                productsLoading: false,
                error: 'Unable to load pricing information. Please try again later.'
            });
        }
    };

    selectProduct = (productId) => {
        this.setState({ selectedProductId: productId, error: null });
    };

    toggleBillingInterval = () => {
        this.setState(prevState => ({
            billingInterval: prevState.billingInterval === 'month' ? 'year' : 'month'
        }));
    };

    calculateSavings = (product) => {
        if (!product.prices.month || !product.prices.year) return null;

        const monthlyPrice = product.prices.month.amount;
        const yearlyPrice = product.prices.year.amount;
        const monthlyEquivalent = monthlyPrice * 12;
        const savings = monthlyEquivalent - yearlyPrice;
        const savingsPercent = Math.round((savings / monthlyEquivalent) * 100);

        return { savings, savingsPercent };
    };

    handleSubscribe = async () => {
        const { selectedProductId, billingInterval, products } = this.state;

        if (!selectedProductId) {
            this.setState({ error: 'Please select a subscription plan' });
            return;
        }

        const selectedProduct = products.find(p => p.productId === selectedProductId);
        if (!selectedProduct) {
            this.setState({ error: 'Selected product not found' });
            return;
        }

        const priceInfo = selectedProduct.prices[billingInterval];
        if (!priceInfo) {
            this.setState({ error: `${billingInterval}ly pricing not available for this product` });
            return;
        }

        try {
            this.setState({ loading: true, error: null });

            const response = await subscriptionsService.createCheckoutSession(
                this.props.organizationId,
                priceInfo.priceId
            );

            if (response.url) {
                window.location.href = response.url;
            } else {
                this.setState({
                    error: 'Missing redirect URL from server',
                    loading: false
                });
            }
        } catch (err) {
            this.setState({
                error: err.message || 'Failed to create checkout session',
                loading: false
            });
        }
    };

    formatPrice = (amount, currency) => {
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency?.toUpperCase() || 'USD',
        });
        return formatter.format(amount);
    };

    formatInterval = (interval, intervalCount) => {
        // Handle interval count (e.g., every 3 months)
        if (intervalCount > 1) {
            return `every ${intervalCount} ${interval}s`;
        }

        // Map interval to readable text
        const intervalMap = {
            'day': 'day',
            'week': 'week',
            'month': 'month',
            'year': 'year'
        };

        return intervalMap[interval] || interval;
    };

    render() {
        const { products, selectedProductId, productsLoading, loading, error, billingInterval, showIntervalToggle } = this.state;

        return (
            <div className={styles.container}>
                <div className={styles.content}>
                    <h1>Subscribe to RepOne</h1>
                    <p>Choose your subscription plan to unlock premium features.</p>

                    {productsLoading ? (
                        <div className={styles.pricingCard}>
                            <p className={styles.price}>Loading...</p>
                        </div>
                    ) : products.length > 0 ? (
                        <>
                            {/* Billing interval toggle */}
                            {showIntervalToggle && (
                                <div className={styles.intervalToggle}>
                                    <button
                                        className={`${styles.toggleButton} ${billingInterval === 'month' ? styles.active : ''}`}
                                        onClick={() => this.setState({ billingInterval: 'month' })}
                                    >
                                        Monthly
                                    </button>
                                    <button
                                        className={`${styles.toggleButton} ${billingInterval === 'year' ? styles.active : ''}`}
                                        onClick={() => this.setState({ billingInterval: 'year' })}
                                    >
                                        Yearly
                                        {billingInterval === 'year' && (
                                            <span className={styles.savingsBadge}>Save up to {
                                                Math.max(...products.map(p => this.calculateSavings(p)?.savingsPercent || 0))
                                            }%</span>
                                        )}
                                    </button>
                                </div>
                            )}

                            <div className={styles.productsGrid}>
                                {products
                                    .filter(product => {
                                        // Only show products that have pricing for the selected interval
                                        if (!product.prices) return false;
                                        const hasInterval = !!product.prices[billingInterval];
                                        if (!hasInterval) {
                                            console.log(`Hiding ${product.productName} - no ${billingInterval} pricing`);
                                        }
                                        return hasInterval;
                                    })
                                    .map((product) => {
                                    const priceInfo = product.prices[billingInterval];
                                    const savings = billingInterval === 'year' ? this.calculateSavings(product) : null;

                                    console.log('Rendering product:', {
                                        productName: product.productName,
                                        billingInterval,
                                        priceInfo,
                                        amount: priceInfo.amount
                                    });

                                    return (
                                        <div
                                            key={product.productId}
                                            className={`${styles.pricingCard} ${selectedProductId === product.productId ? styles.selected : ''}`}
                                            onClick={() => this.selectProduct(product.productId)}
                                        >
                                            <h2>{product.productName}</h2>
                                            <p className={styles.price}>
                                                {this.formatPrice(priceInfo.amount, priceInfo.currency)}
                                                <span className={styles.interval}>
                                                    {' / '}{this.formatInterval(priceInfo.interval, priceInfo.intervalCount)}
                                                </span>
                                            </p>

                                            {billingInterval === 'year' && savings && savings.savings > 0 && (
                                                <div className={styles.savingsText}>
                                                    Save {this.formatPrice(savings.savings, priceInfo.currency)} ({savings.savingsPercent}%)
                                                </div>
                                            )}

                                            {product.productDescription && (
                                                <p className={styles.description}>{product.productDescription}</p>
                                            )}

                                            <ul className={styles.features}>
                                                {/* TODO: Add feature list once features behind paywall are determined */}
                                                <li>Feature 1 (TBD)</li>
                                                <li>Feature 2 (TBD)</li>
                                                <li>Feature 3 (TBD)</li>
                                            </ul>

                                            {selectedProductId === product.productId && (
                                                <div className={styles.selectedBadge}>✓ Selected</div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {error && <div className={styles.error}>{error}</div>}

                            <button
                                className={styles.subscribeButton}
                                onClick={this.handleSubscribe}
                                disabled={loading || !selectedProductId}
                            >
                                {loading ? 'Loading...' : selectedProductId ? 'Subscribe Now' : 'Select a Plan'}
                            </button>

                            <p className={styles.note}>
                                You will be redirected to Stripe to complete your payment securely.
                            </p>
                        </>
                    ) : (
                        <div className={styles.pricingCard}>
                            <p>No subscription plans available at this time.</p>
                            {error && <div className={styles.error}>{error}</div>}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default SubscriptionCheckout;
